import * as THREE from 'three'
import { Section } from './Section.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

export class CTASection extends Section {
  async build(camera, renderer) {
    super.build(camera, renderer)
    this.camera = camera
    this.bgSettledFade = 0

    // Background - Texture
    await this.createTextureBackground()

    // Load interactive group
    await this._loadInteractiveGroup(camera)
    this.doorKnobFrame = 0
    this.ctaScale = 1
    this.autoUpdateDoorKnobY = true
    this.isAutoPlaying = false
    this.autoPlayProgress = 0
    this.autoPlayDuration = 0.8

    this.add(this.contentGroup)

    // Parent bg-plane to the image-seq plane (doorKnobPlane) instead of scene
    if (this.backgroundPlane && this.doorKnobPlane) {
      this.doorKnobPlane.add(this.backgroundPlane)
      this.backgroundPlane.position.set(0, 0, -0.1)
    }

    // Setup drag interaction
    this.setupDragInteraction()

    // Setup CTA message overlay
    this.setupCTAMessageOverlay()
  }

  async _loadInteractiveGroup(camera) {
    const group = new THREE.Group()

    // Load chain model
    const loader = new GLTFLoader()
    const gltf = await loader.loadAsync('./sect04/chain.glb')
    group.add(gltf.scene)
    gltf.scene.scale.setScalar(0.5)
    gltf.scene.position.y = -0.05
    gltf.scene.updateMatrixWorld(true)

    const handle = gltf.scene.getObjectByName('handle')
    const anchor = gltf.scene.getObjectByName('anchor')

    const chains = []
    for (let i = 1; i <= 7; i++) {
      const name = `chain${i.toString().padStart(3, '0')}`
      const obj = gltf.scene.getObjectByName(name)
      if (obj) chains.push(obj)
    }

    const minHandleY = -1.085
    let handleInitialY = 0
    let maxDropY = 0

    // Collect handle meshes for raycasting BEFORE attaching chains
    const handleMeshes = []
    if (handle) {
      handle.traverse((child) => {
        if (child.isMesh) handleMeshes.push(child)
      })
    }

    if (handle) {
      handleInitialY = handle.position.y
      maxDropY = handleInitialY - minHandleY

      handle.position.z = 0

      if (anchor) handle.attach(anchor)
      chains.forEach((link) => {
        if (link) handle.attach(link)
      })
    }

    // Load doorKnob image sequence atlas
    const [manifest, atlasTex] = await Promise.all([
      fetch('./sect04/doorknob-atlas.json').then((r) => r.json()),
      new THREE.TextureLoader().loadAsync('./sect04/doorknob-atlas.webp'),
    ])

    atlasTex.colorSpace = THREE.SRGBColorSpace

    const frameW = Object.values(manifest.frames)[0].frame.w
    const frameH = Object.values(manifest.frames)[0].frame.h
    const cols = manifest.meta.size.w / frameW
    const rows = manifest.meta.size.h / frameH

    atlasTex.repeat.set(1 / cols, 1 / rows)

    const doorKnobFrames = []
    const animation = manifest.animations.doorknob
    for (const name of animation) {
      const f = manifest.frames[name].frame
      const col = f.x / frameW
      const row = f.y / frameH
      doorKnobFrames.push({
        offsetX: col / cols,
        offsetY: (rows - 1 - row) / rows,
      })
    }

    const geometry = new THREE.PlaneGeometry(1, 1)
    const material = new THREE.MeshBasicMaterial({
      map: atlasTex,
      transparent: true,
      alphaTest: 0.5,
      toneMapped: false,
      fog: false,
    })
    const doorKnobPlane = new THREE.Mesh(geometry, material)

    doorKnobPlane.position.set(0, 0.3, 0)
    group.add(doorKnobPlane)

    if (doorKnobFrames.length > 0) {
      const frame = doorKnobFrames[0]
      atlasTex.offset.set(frame.offsetX, frame.offsetY)
    }

    this.contentGroup = group
    this.handle = handle
    this.handleMeshes = handleMeshes
    this.doorKnobPlane = doorKnobPlane
    this.doorKnobAtlasTexture = atlasTex
    this.doorKnobFrames = doorKnobFrames
    this.minHandleY = minHandleY
    this.handleInitialY = handleInitialY
    this.maxDropY = maxDropY
  }

  setupDragInteraction() {
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.isDragging = false
    this.dragPlane = null

    this._onMouseDown = this._onMouseDown.bind(this)
    this._onMouseMove = this._onMouseMove.bind(this)
    this._onMouseUp = this._onMouseUp.bind(this)
    this._onTouchStart = this._onTouchStart.bind(this)
    this._onTouchMove = this._onTouchMove.bind(this)
    this._onTouchEnd = this._onTouchEnd.bind(this)
    this._onClick = this._onClick.bind(this)

    window.addEventListener('mousedown', this._onMouseDown)
    window.addEventListener('mousemove', this._onMouseMove)
    window.addEventListener('mouseup', this._onMouseUp)
    window.addEventListener('click', this._onClick)

    const canvas = document.querySelector('canvas.webgl')
    if (canvas) {
      canvas.addEventListener('touchstart', this._onTouchStart, { passive: false })
      canvas.addEventListener('touchmove', this._onTouchMove, { passive: false })
      canvas.addEventListener('touchend', this._onTouchEnd)
    }
  }

  _getRay(clientX, clientY) {
    this.mouse.x = (clientX / window.innerWidth) * 2 - 1
    this.mouse.y = -(clientY / window.innerHeight) * 2 + 1
    const camera = this.scene.userData.camera || this.camera
    this.raycaster.setFromCamera(this.mouse, camera)
  }

  _onMouseDown(e) {
    this._onDown(e.clientX, e.clientY)
  }

  _onMouseMove(e) {
    this._onMove(e.clientX, e.clientY)
  }

  _onMouseUp() {
    this._onUp()
  }

  _onClick(event) {
    if (!this.isActive) return
    this._getRay(event.clientX, event.clientY)
    const targets = [...this.handleMeshes]
    if (this.doorKnobPlane) targets.push(this.doorKnobPlane)
    const intersects = this.raycaster.intersectObjects(targets, false)
    if (intersects.length > 0) {
      this.triggerAutoPlay()
    }
  }

  triggerAutoPlay() {
    if (!this.handle || this.isDragging || this.isAutoPlaying) return
    this.isAutoPlaying = true
    this.autoPlayProgress = 0
    this.autoPlayDuration = 0.8
  }

  _onTouchStart(e) {
    e.preventDefault()
    const t = e.touches[0]
    this.touchStartX = t.clientX
    this.touchStartY = t.clientY
    this.touchStartTime = performance.now()
    this.isTapCandidate = true
    this._onDown(t.clientX, t.clientY)
  }

  _onTouchMove(e) {
    e.preventDefault()
    const t = e.touches[0]
    if (this.isTapCandidate) {
      const dx = t.clientX - this.touchStartX
      const dy = t.clientY - this.touchStartY
      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        this.isTapCandidate = false
      }
    }
    this._onMove(t.clientX, t.clientY)
  }

  _onTouchEnd() {
    const dt = performance.now() - this.touchStartTime
    const wasTap = this.isTapCandidate && dt < 300
    this.isTapCandidate = false
    this._onUp()
    if (wasTap) {
      this.triggerAutoPlay()
    }
  }

  _onDown(clientX, clientY) {
    if (!this.isActive) return
    this._getRay(clientX, clientY)
    const targets = [...this.handleMeshes]
    if (this.doorKnobPlane) targets.push(this.doorKnobPlane)
    const intersects = this.raycaster.intersectObjects(targets, false)
    if (intersects.length > 0) {
      this.isDragging = true
      const camera = this.scene.userData.camera || this.camera
      const normal = new THREE.Vector3()
      camera.getWorldDirection(normal)
      const handleWorldPos = new THREE.Vector3()
      this.handle.getWorldPosition(handleWorldPos)
      this.dragPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, handleWorldPos)
      document.body.style.cursor = 'grabbing'
    }
  }

  _onMove(clientX, clientY) {
    if (!this.isActive) return

    this._getRay(clientX, clientY)

    if (this.isDragging && this.dragPlane && this.handle) {
      const target = new THREE.Vector3()
      if (this.raycaster.ray.intersectPlane(this.dragPlane, target)) {
        // Convert world target to handle's parent local space
        this.handle.parent.worldToLocal(target)
        this.handle.position.y = Math.min(Math.max(target.y, this.minHandleY), this.handleInitialY)
      }
      return
    }

    const hoverTargets = [...this.handleMeshes]
    if (this.doorKnobPlane) hoverTargets.push(this.doorKnobPlane)
    if (hoverTargets.length > 0) {
      const intersects = this.raycaster.intersectObjects(hoverTargets, false)
      document.body.style.cursor = intersects.length > 0 ? 'grab' : 'default'
    }
  }

  _onUp() {
    this.isDragging = false
    this.dragPlane = null
    document.body.style.cursor = 'default'
  }

  async createTextureBackground() {
    const texture = await new THREE.TextureLoader().loadAsync('./sect04/cta-bg.webp')
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter

    // Calculate plane size based on viewport + parallax margin, locked to 900:1440 aspect ratio
    const viewportAspect = window.innerWidth / window.innerHeight
    const targetAspect = 1440 / 900
    const baseWidth = (20 * viewportAspect + 6) * 0.9
    const baseHeight = (20 + 6) * 0.9
    let width, height
    if (baseWidth / baseHeight > targetAspect) {
      width = baseWidth
      height = width / targetAspect
    } else {
      height = baseHeight
      width = height * targetAspect
    }

    const geometry = new THREE.PlaneGeometry(width, height)
    this.bgMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    })

    this.backgroundPlane = new THREE.Mesh(geometry, this.bgMaterial)
    this.backgroundPlane.scale.setScalar(0.15)
  }

  onResize() {
    if (!this.backgroundPlane) return

    const viewportAspect = window.innerWidth / window.innerHeight
    const targetAspect = 1440 / 900
    const baseWidth = (20 * viewportAspect + 6) * 0.9
    const baseHeight = (20 + 6) * 0.9
    let width, height
    if (baseWidth / baseHeight > targetAspect) {
      width = baseWidth
      height = width / targetAspect
    } else {
      height = baseHeight
      width = height * targetAspect
    }

    this.backgroundPlane.geometry.dispose()
    this.backgroundPlane.geometry = new THREE.PlaneGeometry(width, height)
  }

  updateBackgroundOpacity(cameraZ) {
    if (!this.backgroundPlane || !this.bgMaterial) return

    // Only update opacity — position is controlled by parent (doorKnobPlane)
    let opacity
    if (this.isActive) {
      // Fade in smoothly once camera settles (driven by update())
      opacity = this.bgSettledFade
    } else {
      // Fallback for pre-warm / non-active states
      if (cameraZ <= -70) {
        opacity = 1
      } else {
        opacity = 0
      }
    }

    this.backgroundPlane.visible = opacity > 0.01
    this.bgMaterial.opacity = opacity
  }

  onEnter() {
    super.onEnter()
    this.bgSettledFade = 0

    if (this.contentGroup) {
      this.ctaScale = 3
      this.contentGroup.scale.set(3, 3, 3)
      this.contentGroup.position.y = -0.3
    }

    if (this.autoUpdateDoorKnobY) {
      this._updateDoorKnobY()
    }

    // Hide overlay when entering the section
    this.hideCTAMessage()
  }

  _updateDoorKnobY() {
    if (this.doorKnobPlane) {
      this.doorKnobPlane.position.y = 0.3
    }
  }

  update(delta, scrollProgress, cameraZ, isSettled) {
    if (!this.isActive) return

    // Fade background in once camera settles at CTA
    if (isSettled && cameraZ <= -68) {
      this.bgSettledFade = Math.min(this.bgSettledFade + delta * 2, 1)
      if (this.backgroundPlane) this.backgroundPlane.scale.setScalar(0.15)
    } else {
      this.bgSettledFade = Math.max(this.bgSettledFade - delta * 2, 0)
      if (this.backgroundPlane) {
        if (this.bgSettledFade > 0) {
          // Leaving: scale up as we fade out (0.15 → 0.30)
          const t = 1 - this.bgSettledFade
          this.backgroundPlane.scale.setScalar(0.15 + t * 0.15)
        } else {
          // Approaching: keep base scale
          this.backgroundPlane.scale.setScalar(0.15)
        }
      }
    }

    // Texture background - no per-frame update needed
    if (this.autoUpdateDoorKnobY) {
      this._updateDoorKnobY()
    }

    // Auto-play animation
    if (this.isAutoPlaying && this.handle) {
      this.autoPlayProgress += delta / this.autoPlayDuration
      if (this.autoPlayProgress >= 1) {
        this.autoPlayProgress = 1
        this.isAutoPlaying = false
      }
      const ease = 1 - Math.pow(1 - this.autoPlayProgress, 3)
      this.handle.position.y = this.handleInitialY - (this.handleInitialY - this.minHandleY) * ease
    }

    // Spring handle back to initial position when not dragging and not auto-playing
    if (!this.isDragging && !this.isAutoPlaying && this.handle) {
      this.handle.position.y += (this.handleInitialY - this.handle.position.y) * 0.02
    }

    // Update doorKnob frame based on handle pull
    if (this.doorKnobFrames.length > 0 && this.doorKnobPlane && this.handle) {
      const totalFrames = this.doorKnobFrames.length
      const pull = this.handleInitialY - this.handle.position.y
      const progress = Math.min(Math.max(pull / this.maxDropY, 0), 1)
      const targetFrame = progress * (totalFrames - 1)
      const frameIndex = Math.round(targetFrame)

      if (frameIndex !== this.doorKnobFrame) {
        this.doorKnobFrame = frameIndex
        const frame = this.doorKnobFrames[frameIndex]
        this.doorKnobAtlasTexture.offset.set(frame.offsetX, frame.offsetY)

        // Show overlay when reaching the last frame
        if (frameIndex === totalFrames - 1) {
          this.showCTAMessage()
        }
      }
    }
  }

  setupCTAMessageOverlay() {
    this.ctaMessageOverlay = document.getElementById('cta-message-overlay')
    this.ctaMessageBtn = this.ctaMessageOverlay?.querySelector('.cta-message-btn')
    this.ctaMessageCloseBtn = this.ctaMessageOverlay?.querySelector('.cta-message-close')

    if (this.ctaMessageBtn) {
      this._onCTAMessageBtnClick = this._onCTAMessageBtnClick.bind(this)
      this.ctaMessageBtn.addEventListener('click', this._onCTAMessageBtnClick)
    }

    if (this.ctaMessageCloseBtn) {
      this._onCTAMessageClose = this._onCTAMessageClose.bind(this)
      this.ctaMessageCloseBtn.addEventListener('click', this._onCTAMessageClose)
    }
  }

  showCTAMessage() {
    if (this.ctaMessageOverlay) {
      this.ctaMessageOverlay.classList.add('visible')
    }
  }

  hideCTAMessage() {
    if (this.ctaMessageOverlay) {
      this.ctaMessageOverlay.classList.remove('visible')
    }
  }

  _onCTAMessageBtnClick() {
    this.hideCTAMessage()
  }

  _onCTAMessageClose() {
    this.hideCTAMessage()
  }

  onExit() {
    super.onExit()
  }

  destroy() {
    window.removeEventListener('mousedown', this._onMouseDown)
    window.removeEventListener('mousemove', this._onMouseMove)
    window.removeEventListener('mouseup', this._onMouseUp)
    window.removeEventListener('click', this._onClick)

    const canvas = document.querySelector('canvas.webgl')
    if (canvas) {
      canvas.removeEventListener('touchstart', this._onTouchStart)
      canvas.removeEventListener('touchmove', this._onTouchMove)
      canvas.removeEventListener('touchend', this._onTouchEnd)
    }

    if (this.ctaMessageBtn) {
      this.ctaMessageBtn.removeEventListener('click', this._onCTAMessageBtnClick)
    }

    if (this.ctaMessageCloseBtn) {
      this.ctaMessageCloseBtn.removeEventListener('click', this._onCTAMessageClose)
    }

    super.destroy()
  }
}
